import { useState, useEffect } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Separator } from '@/components/ui/separator';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { supabase } from '@/integrations/supabase/client';
import { invokeFunctionWithAuth } from '@/lib/supabase/functions';
import { useLocation } from '@/providers/LocationProvider';
import { Plus, Star, Trash2 } from 'lucide-react';
import { FilmmakerDialog } from '@/components/admin/FilmmakerDialog';
import { toast } from 'sonner';
import type { Filmmaker } from '@/types';

export default function AdminContractors() {
  const location = useLocation();
  const [filmmakers, setFilmmakers] = useState<Filmmaker[]>([]);
  const [loading, setLoading] = useState(true);
  const [locationId, setLocationId] = useState<string>('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedFilmmaker, setSelectedFilmmaker] = useState<Filmmaker | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [filmakerToDelete, setFilmakerToDelete] = useState<Filmmaker | null>(null);

  useEffect(() => {
    fetchFilmmakers();
  }, [location]);

  const fetchFilmmakers = async () => {
    try {
      const { data: locationData } = await supabase
        .from('locations')
        .select('id')
        .eq('code', location)
        .single();

      if (!locationData) return;
      
      setLocationId(locationData.id);

      const { data } = await supabase
        .from('filmmakers')
        .select(`
          *,
          profiles!inner(full_name, email, phone, avatar_url)
        `)
        .eq('location_id', locationData.id)
        .order('created_at', { ascending: true });

      // Merge profiles data into filmmakers
      let filmmakersWithProfiles = data?.map((f: any) => ({
        ...f,
        full_name: f.profiles.full_name,
        email: f.profiles.email,
        phone: f.profiles.phone,
        avatar_url: f.profiles.avatar_url,
        role: 'filmmaker' as const,
      })) || [];

      // Fallback: se não houver contractors para a location atual, listar TODOS via edge function (service role)
      if (filmmakersWithProfiles.length === 0) {
        try {
          const { data, error } = await invokeFunctionWithAuth('get-filmmakers', {});
          if (!error && data?.filmmakers) {
            filmmakersWithProfiles = data.filmmakers.map((f: any) => ({
              ...f,
              full_name: f.profiles.full_name,
              email: f.profiles.email,
              phone: f.profiles.phone,
              avatar_url: f.profiles.avatar_url,
              role: 'filmmaker' as const,
            }));
          }
        } catch (e) {
          console.warn('Fallback get-filmmakers failed:', e);
        }
      }

      // Fallback 2: se ainda vazio, derive a lista a partir de user_roles + profiles (mesmo sem row em filmmakers)
      if (filmmakersWithProfiles.length === 0) {
        const { data: profilesAsFilmmakers } = await supabase
          .from('profiles')
          .select(`
            id,
            full_name,
            email,
            phone,
            avatar_url,
            user_roles!inner(role, location_id)
          `)
          .eq('user_roles.role', 'filmmaker')
          .order('full_name', { ascending: true });

        filmmakersWithProfiles = profilesAsFilmmakers?.map((p: any) => ({
          id: p.id,
          full_name: p.full_name,
          email: p.email,
          phone: p.phone,
          avatar_url: p.avatar_url,
          location_id: p.user_roles?.[0]?.location_id || null,
          is_active: true,
          rating: 5.0,
          bio: '',
          role: 'filmmaker' as const,
        })) || [];
      }

      setFilmmakers(filmmakersWithProfiles);
    } catch (error) {
      console.error('Error fetching filmmakers:', error);
      toast.error('Failed to load filmmakers');
    } finally {
      setLoading(false);
    }
  };

  const handleAddFilmmaker = () => {
    setSelectedFilmmaker(null);
    setDialogOpen(true);
  };

  const handleEditFilmmaker = (filmmaker: Filmmaker) => {
    setSelectedFilmmaker(filmmaker);
    setDialogOpen(true);
  };

  const handleDeleteClick = (filmmaker: Filmmaker) => {
    setFilmakerToDelete(filmmaker);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!filmakerToDelete) return;

    try {
      // Delete filmmaker record (this will cascade to related tables)
      const { error } = await supabase
        .from('filmmakers')
        .delete()
        .eq('id', filmakerToDelete.id);

      if (error) throw error;

      toast.success('Filmmaker deleted successfully');
      fetchFilmmakers();
    } catch (error: any) {
      console.error('Error deleting filmmaker:', error);
      toast.error(error.message || 'Failed to delete filmmaker');
    } finally {
      setDeleteDialogOpen(false);
      setFilmakerToDelete(null);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-3xl font-bold tracking-tight">Contractors</h2>
          <p className="text-muted-foreground">
            Manage filmmakers and their profiles
          </p>
        </div>
        <Button onClick={handleAddFilmmaker}>
          <Plus className="h-4 w-4 mr-2" />
          Add Contractor
        </Button>
      </div>

      {loading ? (
        <Card className="p-6">
          <p className="text-center text-muted-foreground">Loading contractors...</p>
        </Card>
      ) : filmmakers.length === 0 ? (
        <Card className="p-6">
          <p className="text-center text-muted-foreground">No contractors found</p>
        </Card>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filmmakers.map((filmmaker) => {
            const initials = filmmaker.full_name?.split(' ').map(n => n[0]).join('') || 'FM';
            
            return (
              <Card key={filmmaker.id} className="p-4">
                <div className="flex items-start gap-3 mb-3">
                  <Avatar className="h-12 w-12">
                    <AvatarImage src={filmmaker.avatar_url} />
                    <AvatarFallback>{initials}</AvatarFallback>
                  </Avatar>

                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="font-semibold truncate">
                        {filmmaker.full_name || 'Unnamed'}
                      </h3>
                      <Badge variant={filmmaker.is_active ? 'default' : 'secondary'} className="shrink-0">
                        {filmmaker.is_active ? 'Active' : 'Inactive'}
                      </Badge>
                    </div>

                    <div className="flex items-center gap-1 mb-2">
                      <Star className="h-4 w-4 fill-yellow-400 text-yellow-400" />
                      <span className="text-sm font-medium">
                        {Number(filmmaker.rating).toFixed(1)}
                      </span>
                    </div>

                    <p className="text-xs text-muted-foreground truncate">
                      {filmmaker.phone}
                    </p>
                  </div>
                </div>

                {filmmaker.bio && (
                  <p className="text-sm text-muted-foreground mb-3 line-clamp-2">
                    {filmmaker.bio}
                  </p>
                )}

                <Separator className="my-3" />

                <div className="flex gap-2">
                  <Button 
                    size="sm" 
                    variant="outline" 
                    className="flex-1"
                    onClick={() => handleEditFilmmaker(filmmaker)}
                  >
                    Edit
                  </Button>
                  <Button 
                    size="sm" 
                    variant="ghost"
                    onClick={() => handleDeleteClick(filmmaker)}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </Card>
            );
          })}
        </div>
      )}

      <FilmmakerDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        filmmaker={selectedFilmmaker}
        locationId={locationId}
        onSuccess={fetchFilmmakers}
      />

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Filmmaker</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete {filmakerToDelete?.full_name}? This action cannot be undone and will remove all associated data.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteConfirm}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
